import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { WebMercatorViewport } from 'viewport-mercator-project';
import { Spinner } from '@surfline/quiver-react';
import { slugify } from '@surfline/web-common';
import { spotReportPath } from '@surfline/web-common/dist/paths/kbyg';
import SpotMap from '../../SpotMap';
import SpotList from '../../SpotList';
import GeonameMapPageMeta from '../../../components/GeonameMapPageMeta';
import { scrollToSpot } from '../../../actions/animations';
import { setMapLocation, fetchLocationView } from '../../../actions/map';
import { navigatedToPage } from '../../../actions/segmentEvents';
import { leaveGeonameMapRoute } from '../../../actions/routes';
import locationViewPropShape from '../../../propTypes/locationView';
import spotPropShape from '../../../propTypes/spot';
import { SYNC_ONLY_ON_MAP_INTERACTIONS } from '../../../propTypes/map/mapUrlSync';
import mapOptions from '../../../utils/mapOptions';
import { camsAndForecastsPaths } from '../../../utils/urls/camsAndForecastsPath';
import { getMapPageHeight, getMapPageWidth } from '../../../utils/dimensions';

class GeonameMap extends Component {
  static fetch = [({ geonameId }, { cookies }) => fetchLocationView(geonameId, cookies)];

  constructor(props) {
    super(props);

    this.state = {
      mapPageHeight: {
        height: '0px',
      },
    };

    const { doNavigatedToPage } = this.props;
    doNavigatedToPage('Map', {
      category: 'cams & forecasts',
      channel: 'cams & forecasts',
    });
  }

  componentDidMount() {
    const { locationView, match, doFetchLocationView } = this.props;
    this.setMapPageHeight();
    if (!locationView.boundingBox || !locationView.boundingBox.west) {
      doFetchLocationView(match.params.geonameId);
    } else {
      this.fitBoundsAndSetMapLocation(locationView.boundingBox);
    }

    if (document) document.body.classList.add('sl-hide-ze-widget');
  }

  componentDidUpdate(prevProps) {
    const { locationView, match, doFetchLocationView } = this.props;

    /* fetch location view on client route change */
    if (match.params.geonameId !== prevProps.match.params.geonameId) {
      doFetchLocationView(match.params.geonameId);
    }
    /* set map location on bounding box change */
    if (locationView.boundingBox !== prevProps.locationView.boundingBox) {
      this.fitBoundsAndSetMapLocation(locationView.boundingBox);
    }
  }

  componentWillUnmount() {
    const { doLeaveGeonameMapRoute } = this.props;
    if (document) document.body.classList.remove('sl-hide-ze-widget');
    doLeaveGeonameMapRoute();
  }

  setMapPageHeight = () => this.setState({ mapPageHeight: getMapPageHeight() });

  fitBoundsAndSetMapLocation = boundingBox => {
    const { doSetMapLocation } = this.props;

    const height = this.mapRef.clientHeight ? this.mapRef.clientHeight : 1000;
    const width = this.mapRef.clientWidth ? this.mapRef.clientWidth : 1000;
    const { north: lat0, west: lon0, south: lat1, east: lon1 } = boundingBox;
    const viewport = new WebMercatorViewport({ width, height }).fitBounds([
      [lon0, lat0],
      [lon1, lat1],
    ]);
    doSetMapLocation({
      center: { lat: viewport.latitude, lon: viewport.longitude },
      zoom: viewport.zoom,
    });
  };

  goToSpot = spot => {
    const { history } = this.props;
    return history.push(spotReportPath(slugify(spot.name), spot._id));
  };

  render() {
    const { mapPageHeight } = this.state;
    const { locationView, spots, loadingSpots, doScrollToSpot } = this.props;
    const urlPath =
      locationView && locationView.url && `${camsAndForecastsPaths.base}${locationView.url}`;
    return (
      <div className="sl-map-page" style={mapPageHeight}>
        <GeonameMapPageMeta name={locationView.taxonomy.name || ''} urlPath={urlPath} />
        <div
          className="sl-right-rail"
          ref={ref => {
            this.mapRef = ref;
          }}
        >
          <SpotMap
            mapOptions={mapOptions}
            showBottomControls
            clusterMaxZoom={11}
            syncUrl={SYNC_ONLY_ON_MAP_INTERACTIONS}
            storeLocationInSession
            onClickSpotMarker={getMapPageWidth() < 992 ? doScrollToSpot : this.goToSpot}
          />
        </div>
        <div className="sl-left-rail">
          {!locationView.loading && !loadingSpots && spots.length > 0 ? (
            <div>
              <SpotList locationView={locationView} />
            </div>
          ) : (
            <div className="sl-geonamemap__spinner-wrapper">
              <Spinner />
            </div>
          )}
        </div>
      </div>
    );
  }
}

GeonameMap.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      geonameId: PropTypes.string,
    }),
  }).isRequired,
  doFetchLocationView: PropTypes.func.isRequired,
  doScrollToSpot: PropTypes.func.isRequired,
  doSetMapLocation: PropTypes.func.isRequired,
  doNavigatedToPage: PropTypes.func.isRequired,
  doLeaveGeonameMapRoute: PropTypes.func.isRequired,
  locationView: locationViewPropShape,
  spots: PropTypes.arrayOf(spotPropShape),
  loadingSpots: PropTypes.bool,
  history: PropTypes.shape({ push: PropTypes.func }).isRequired,
};

GeonameMap.defaultProps = {
  locationView: {
    boundingBox: {},
    taxonomy: {
      name: null,
      location: {
        coordinates: [],
      },
    },
  },
  spots: [],
  loadingSpots: false,
};

export default connect(
  state => ({
    locationView: state.map.locationView,
    spots: state.map.spots,
    loadingSpots: state.status.loading,
  }),
  dispatch => ({
    doFetchLocationView: geonameId => dispatch(fetchLocationView(geonameId)),
    doSetMapLocation: location => dispatch(setMapLocation(location, false, true)),
    doScrollToSpot: spot => dispatch(scrollToSpot(spot._id)),
    doNavigatedToPage: (name, properties) => dispatch(navigatedToPage(name, properties)),
    doLeaveGeonameMapRoute: () => dispatch(leaveGeonameMapRoute()),
  })
)(GeonameMap);
